import type { OrderPriceResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { feesAtom, paymentDetailAtom } from "Order/Atom/paymentAtom";
import {
	ElementType,
	PaymentLegend,
} from "Order/Modules/Payment/Components/PaymentLegend";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { VerticalBarChart } from "Shared/Components/Charts/VerticalBarChart";
import { Box, Stack, Typography } from "@mui/material";
import { useAtom } from "jotai";

type PaymentProgressWidgetProps = {
	currency?: string;
	country?: string;
	orderPrice: OrderPriceResponseDto;
};

const paymentResource = Resources.base.payment.shared;

export function PaymentProgressWidget({
	currency,
	country,
	orderPrice,
}: PaymentProgressWidgetProps) {
	const { t } = useResource();
	const [paymentDetail] = useAtom(paymentDetailAtom);
	const [fees] = useAtom(feesAtom);

	const elements = [
		{
			color: "success.main",
			label: paymentResource.totalPaid,
			value: paymentDetail?.totalCustomerPayment ?? 0,
			type: ElementType.bar,
		},
		{
			color: "warning.main",
			label: paymentResource.depositPaid,
			value: paymentDetail?.totalDeposit ?? 0,
			type: ElementType.bar,
		},
		{
			color: "error.main",
			label: paymentResource.totalDeposit,
			value: fees?.depositRequirementAmount ?? 0,
			type: ElementType.breakpoint,
		},
		{
			color: "secondary.dark",
			label: paymentResource.discount,
			value: fees?.serviceFeeDiscountAmount ?? 0,
			type: ElementType.bar,
		},
		{
			color: "primary.main",
			label: paymentResource.total,
			value: orderPrice?.totalPrice ?? 0,
		},
	];

	return (
		<BaseCard>
			<Stack>
				<Typography variant="h5">
					{t(paymentResource.paymentProgress)}
				</Typography>
				{country && currency && (
					<PaymentLegend
						elements={elements}
						currency={currency}
						country={country}
					/>
				)}
				<Box mt={2} mb={1} alignSelf="flex-end">
					<Typography variant="body2">
						{t(paymentResource.total)}:{" "}
						{formatCurrency(country, currency, orderPrice?.totalPrice ?? 0)}
					</Typography>
				</Box>
				<VerticalBarChart
					elements={elements}
					total={paymentDetail?.partnerDue ?? 0}
				/>
			</Stack>
		</BaseCard>
	);
}
